// Mini recap interstitial shown between skills inside Beginner Mode.
// Fires after `markDone()`: instead of jumping straight into the next
// skill, we pause for one screen of momentum-building feedback.
//
// Shape:
//   ✓ You learned: <skill label>
//   Next up:        <next skill label>
//   Tier progress:  X / 20  ████░░░░  (per-tier bar)
//   Overall:        N / 100 done
//   [Continue → skill #N+1]
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { useBeginnerMode } from "@/hooks/useBeginnerMode";
import { AI_MASTERY_PATH, MASTERY_TIERS } from "@/data/aiMasteryPath";

const SkillRecapCard = () => {
  const { active, pendingRecapNum, completed, continueAfterRecap } = useBeginnerMode();

  if (!active || pendingRecapNum == null) return null;

  const justDone = AI_MASTERY_PATH.find((s) => s.num === pendingRecapNum);
  if (!justDone) return null;

  const nextNum = Math.min(pendingRecapNum + 1, AI_MASTERY_PATH.length);
  const next = AI_MASTERY_PATH.find((s) => s.num === nextNum);
  const tierMeta = MASTERY_TIERS[justDone.tier];

  // Per-tier progress.
  const tierSkills = AI_MASTERY_PATH.filter((s) => s.tier === justDone.tier);
  const tierDone = tierSkills.filter((s) => completed.includes(s.num)).length;
  const tierPct = Math.round((tierDone / tierSkills.length) * 100);

  const overallPct = Math.round((completed.length / AI_MASTERY_PATH.length) * 100);
  const tierFinished = tierDone >= tierSkills.length;

  return (
    // Renders as a full-screen overlay ABOVE the BeginnerShell content
    // (same z-index since they're siblings, but this mounts only when
    // pendingRecapNum is set — so the shell's content is visually
    // obscured by this card while the user dwells on their progress).
    <div className="fixed inset-0 z-[105] bg-background/95 backdrop-blur-sm overflow-y-auto grid place-items-center px-4 py-8">
      <div className="w-full max-w-md rounded-2xl border border-primary/30 bg-card p-6 shadow-2xl">
        {/* Big checkmark */}
        <div className="flex flex-col items-center text-center mb-5">
          <div className="w-16 h-16 rounded-full bg-primary/15 grid place-items-center mb-3">
            <Check className="w-8 h-8 text-primary" />
          </div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
            Skill #{justDone.num} complete
          </p>
          <h2 className="font-mono font-extrabold text-lg text-foreground">
            {justDone.label}
          </h2>
        </div>

        {/* Tier progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-wider ${tierMeta.pill}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${tierMeta.dot}`} aria-hidden />
              {tierMeta.headline}
            </span>
            <span className="text-[10px] font-mono text-muted-foreground">
              {tierDone} / {tierSkills.length}
            </span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-700"
              style={{ width: `${tierPct}%` }}
            />
          </div>
          {tierFinished && (
            <p className="mt-2 text-[11px] font-mono text-primary text-center">
              <Sparkles className="inline w-3 h-3 mr-1" />
              You finished the {tierMeta.headline} tier!
            </p>
          )}
        </div>

        {/* Overall progress */}
        <div className="mb-5 rounded-lg border border-border bg-secondary/30 p-3">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
            Overall
          </p>
          <p className="text-sm font-mono text-foreground">
            <span className="font-bold text-primary">{completed.length}</span>
            <span className="text-muted-foreground"> / {AI_MASTERY_PATH.length} done · {overallPct}%</span>
          </p>
        </div>

        {/* Next up */}
        {next && (
          <div className="mb-5 rounded-lg border border-primary/30 bg-primary/5 p-3">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
              Next up · skill #{next.num}
            </p>
            <p className="text-sm font-mono font-bold text-foreground">{next.label}</p>
          </div>
        )}

        <button
          type="button"
          onClick={continueAfterRecap}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-mono font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default SkillRecapCard;
